import React from "react";
import { numberFormat, formatToCompanyTimezone } from "../../../lib/functions";
import "./SalesVoucherThermal.css";

function mergeVoucherItems(items) {
  const map = {};
  items.forEach((item) => {
    const key = [
      item.item_id,
      item.item_description || "",
      item.batch_id || 0,
      item.packing_name || "",
      item.hsn_sac_code || "",
      Number(item.item_rate || 0).toFixed(2),
      Number(item.item_discount_per || 0),
      Number(item.item_tax_per || 0),
      Number(item.item_rate) === 0 ? "BONUS" : "NORMAL",
    ].join("|");

    if (!map[key]) {
      map[key] = {
        ...item,
        item_qty: Number(item.item_qty || 0),
        item_total: Number(item.item_total || 0),
      };
    } else {
      map[key].item_qty += Number(item.item_qty || 0);
      map[key].item_total += Number(item.item_total || 0);
    }
  });
  return Object.values(map);
}

const isBonusItem = (item) => Number(item.item_rate) === 0;

const SalesVoucherThermal = ({
  voucher,
  institution,
  authInfo,
  printMode = false,
  hideBonusItems = false,
}) => {
  if (!voucher || !authInfo) return null;

  const isIndia = authInfo?.country?.toLowerCase() === "india";
const isIndiaGst = authInfo?.gst_enabled === "yes";
  const taxLabel = isIndia ? "GST" : "VAT";
  const isUAE =
    authInfo?.country?.trim().toLowerCase() === "united arab emirates";

  const merged = mergeVoucherItems(voucher.details || []);
  const visibleItems = hideBonusItems
    ? merged.filter((item) => !isBonusItem(item))
    : merged;
  const hasBonusItems = merged.some(isBonusItem);
  const serviceCost = Number(voucher.other_service_cost || 0);
  const dateStr = formatToCompanyTimezone(voucher.created_date)?.split(" ")[0];

  // helper for info rows
  const InfoRow = ({ label, value }) =>
    value ? (
      <div className="ti-row">
        <span className="ti-label">{label}</span>
        <span className="ti-colon">:</span>
        <span className="ti-val">{value}</span>
      </div>
    ) : null;

  // helper for total rows
  const TotalRow = ({ label, value }) => (
    <div className="tt-row">
      <span className="tt-label">{label}</span>
      <span className="tt-value">{value}</span>
    </div>
  );

  return (
    <div className="thermal-voucher">

      {/* ── Header ── */}
      <div className="thermal-header">
        <h2>{authInfo?.institution_name}</h2>
        {institution?.street_address && <p>{institution.street_address}</p>}
        {institution?.phone_no && <p>Ph: +{institution.phone_no}</p>}
        {institution?.vat_license && (
          <p>{isUAE ? "TRN No: " : "Tax Reg No: "}{institution.vat_license}</p>
        )}
      </div>

      <hr className="thermal-divider" />

      {/* ── Invoice info ── */}
      <div className="thermal-info">
        <InfoRow label="Invoice" value={voucher.sale_voucher_no} />
        <InfoRow label="Date" value={dateStr} />
        <InfoRow label="Due Date" value={voucher.due_date} />
        <InfoRow label="Customer" value={voucher.acc_name} />
        <InfoRow label="Phone" value={voucher.contact_no ? `+${voucher.contact_no}` : null} />
        {voucher.vat_no && (
          <InfoRow
            label={isUAE ? "TRN No" : "Tax Reg"}
            value={voucher.vat_no}
          />
        )}
      </div>

      <hr className="thermal-divider" />

      {/* ── Items ── */}
      <table className="thermal-items">
        <thead>
          <tr>
            <th className="col-name">Item</th>
            <th className="col-qty">Qty</th>
            <th className="col-amount">Amount</th>
          </tr>
        </thead>
        <tbody>
          {visibleItems.map((item, i) => (
            <tr key={i} className={isBonusItem(item) ? "bonus-row" : ""}>
              <td className="col-name">
                <div>{item.item_name}</div>

                {item.batch_name && (
                  <div className="thermal-item-description">
                    Batch: {item.batch_name}
                  </div>
                )}

                {item.item_description && (
                  <div className="thermal-item-description">
                    {item.item_description}
                  </div>
                )}

                {isBonusItem(item) && (
                  <span className="bonus-badge">BONUS</span>
                )}
              </td>
              <td className="col-qty">
                {item.item_qty} {item.unit_symbol}
              </td>
              <td className="col-amount">
                {numberFormat(
                  item.item_total ?? item.item_qty * item.item_rate
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {hasBonusItems && !hideBonusItems && (
        <p style={{ fontSize: 10, margin: "4px 0 0", color: "#555" }}>
          * Bonus items supplied free of cost.
        </p>
      )}

      <hr className="thermal-divider" />

      {/* ── Totals ── */}
      <div className="thermal-total">
        <TotalRow label="Sub Total" value={numberFormat(voucher.sub_total)} />

        {Number(voucher.total_discount) > 0 && (
          <TotalRow
            label="Discount"
            value={`- ${numberFormat(voucher.total_discount)}`}
          />
        )}

        {isIndiaGst ? (
          <>
            {voucher.place_of_supply_type === "intra" && (
              <>
                <TotalRow label="CGST" value={numberFormat(voucher.cgst_amount)} />
                <TotalRow label="SGST" value={numberFormat(voucher.sgst_amount)} />
              </>
            )}
            {voucher.place_of_supply_type === "inter" && (
              <TotalRow label="IGST" value={numberFormat(voucher.igst_amount)} />
            )}
            {voucher.gst_calculated === "no" && (
              <TotalRow
                label={`Total ${taxLabel}`}
                value={numberFormat(voucher.total_tax)}
              />
            )}
          </>
        ) : (
          !isIndia && (
            <TotalRow
              label={`Total ${taxLabel}`}
              value={numberFormat(voucher.total_tax)}
            />
          ))}

        {serviceCost > 0 && (
          <TotalRow
            label="Additional Expense"
            value={numberFormat(serviceCost)}
          />
        )}
      </div>

      {/* ── Grand Total ── */}
      <div className="thermal-grand">
        <span className="tt-label">GRAND TOTAL ({authInfo?.currency})</span>
        <span className="tt-value">{numberFormat(voucher.total_amount)}</span>
      </div>

      <div className="thermal-total">
        <TotalRow label="Paid" value={numberFormat(voucher.paid_total || 0)} />
        <TotalRow label="Balance Due" value={numberFormat(voucher.due_amount || 0)} />
      </div>

      <hr className="thermal-divider" />

      {/* ── Footer ── */}
      <div className="thermal-footer">
        <div>Thank You</div>
        <div>Visit Again</div>
      </div>

    </div>
  );
};

export default SalesVoucherThermal;